import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, Alert, Typography, Skeleton,
} from '@mui/material';
import api from '../api/axios';

// Pick which game of the studio's itch.io account this dashboard shows.
export default function ItchSourceDialog({ open, onClose, onSave, initial, saving }) {
  const [selected, setSelected] = useState(null);
  const { data, isLoading, error } = useQuery({
    queryKey: ['itch-games'],
    queryFn: () => api.get('/itch/games').then((r) => r.data),
    enabled: open,
  });

  const games = data?.games || [];
  const choose = games.find((g) => g.id === selected);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      TransitionProps={{ onEnter: () => setSelected(initial?.gameId ?? null) }}
    >
      <DialogTitle>{initial ? 'Change itch.io game' : 'Connect itch.io game'}</DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {[0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={52} />)}
          </Box>
        ) : error ? (
          <Alert severity="error" variant="outlined">{error.response?.data?.error || error.message}</Alert>
        ) : data && !data.configured ? (
          <Alert severity="info" variant="outlined">
            The server has no itch.io API key. Create one at itch.io → Settings → API keys and set it as
            <b> ITCH_API_KEY</b> in the server environment (Dokploy), then redeploy.
          </Alert>
        ) : games.length === 0 ? (
          <Typography color="text.secondary">This itch.io account has no games.</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {games.map((g) => {
              const active = g.id === selected;
              return (
                <Box key={g.id} onClick={() => setSelected(g.id)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5, p: 1, borderRadius: 1, cursor: 'pointer',
                    border: '1px solid', borderColor: active ? 'primary.main' : 'divider',
                    bgcolor: active ? 'rgba(210,122,90,0.08)' : 'transparent',
                    '&:hover': { borderColor: active ? 'primary.main' : 'rgba(235,231,225,0.24)' },
                  }}
                >
                  <Box sx={{ width: 50, height: 40, borderRadius: 0.75, bgcolor: '#1f1d1b', overflow: 'hidden', flexShrink: 0 }}>
                    {g.coverUrl && <img src={g.coverUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography fontWeight={600} noWrap>{g.title}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {g.views.toLocaleString()} views · {g.downloads.toLocaleString()} downloads{!g.published && ' · draft'}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button variant="contained" disabled={!choose || saving}
          onClick={() => onSave({ gameId: choose.id, title: choose.title })}
        >
          {saving ? 'Saving…' : 'Use this game'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
